<template>
  <div class="chat-container">
    <div class="chat-area scroller-thin" ref="chatArea" @scroll="onChatScroll">
      <div class="chat-content">
        <div v-for="(messageInfo, index) of chatMessageList" :key="messageInfo.id" class="conversation-message">
          <div :class="['message-main', { first: index == 0, 'user-role': isMessageFromUser(messageInfo), 'assistant-role': isMessageFromAssistant(messageInfo) }]">
            <div class="message-body">
              <div v-if="!messageInfo.hasData && messageInfo.streaming && !thinkingMessage" class="text-muted">
                Thinking...
              </div>

              <div v-html="messageInfo.renderedHtml" v-if="!messageInfo.streaming && messageInfo.hasData" class="conversation-content">
                &nbsp;
              </div>
              <div class="conversation-content" v-if="messageInfo.streaming" :ref="`streamingMessage_${messageInfo.id}`"></div>

              <div class="message-footer">
                <div class="message-user">
                  {{messageInfo.userTitle}}
                </div>
                <div class="message-info" v-if="messageInfo.isAgent && false">
                  <LabelBadge labelClass="label-ai-agent">AI AGENT</LabelBadge>
                </div>
                <div class="message-time">
                  {{displayMessageTimestamp(messageInfo.createdAt)}}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="thinkingMessage" class="text-muted">
          {{thinkingMessage}}
        </div>
      </div>
    </div>
    <div class="chat-scroll-to-bottom" v-if="displayScrollToBottomButton && hasConversation">
      <div class="col-12 text-center">
        <button class="btn btn-sm btn-light" @click="scrollChatToBottom"><i class="fas fa-arrow-down" /></button>
      </div>
    </div>
    <div class="chat-entry-container" v-if="hasConversation">
      <FormInputTextArea v-model="chatMessage" @enterPressed="submitMessage" detectEnterKeypress :resizeEnabled="false" placeholder="Message Sales Manager" :readonly="isChatDisabled" />
      <div class="chat-disclaimer row">
        <div class="col-12 text-center">
          <small class="text-muted"><i class="far fa-info-circle" /> AI generated insights may be inaccurate or misleading. Please verify important information.</small>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chat-container {
  position: relative;
  width: 100%;
  height: 68dvh;
}
.chat-area {
  flex: 1;
  height: 100%;
  width: 100%;
  padding: 0 20px;
  overflow-x: hidden;
  overflow-y: scroll;
}
.chat-content {
  position: relative;
  width: 100%;
  padding: 10px 10px 160px 10px;
}
.chat-entry-container {
  position: absolute;
  left: 50%;
  min-width: 750px;
  width: 50%;
  max-width: 1000px;
  height: 115px;
  bottom: 20px;
  padding: 15px;

  background: #ffffff;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
  border-radius: 8px;

  transform: translateX(-50%);
}
.chat-disclaimer {
  text-align: center;
}
.chat-scroll-to-bottom {
  position: absolute;
  bottom: 140px;
  left: 50%;
  transform: translateX(-50%);
}
.chat-scroll-to-bottom button {
  border-radius: 50%;
  border: 1px solid #ccc;
  width: 40px;
  height: 40px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
}

.conversation-message {
  margin-bottom: 6px;
  display: flex;
  flex-direction: row;
  padding-top: 10px;
}
.chat-content .conversation-message .message-main:not(.first) {
  /*border-top: 1px solid #efefef;*/
}

.message-main {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.message-main .message-body {
  padding: 12px 14px 4px 14px;
}

.message-main.assistant-role .message-body {
  background: #F8F8F7;
  border-radius: $layout-border-radius;
  
  width: fit-content;
  border-bottom-left-radius: 0px;
}

.message-main.user-role .message-body {
  text-align: right;
  background: #baffc4;
  border-radius: $layout-border-radius;
  width: fit-content;
  margin-left: auto;
  border-bottom-right-radius: 0px;
}
.message-main.user-role .message-body .conversation-content ::v-deep p {
  margin: 0;
}
.message-main.user-role .message-footer {
  justify-content: flex-end;
}
.message-main .message-footer {
  display: flex;
  flex-direction: row;
  font-size: 10px;
  gap: 1px;
  margin-top: 6px;
  width: 100%;
  opacity: 0.75;
}
.message-main .message-user {
  font-weight: bold;
  margin-right: 6px;
  font-size: 10px;
}
.message-main .message-time {
  /*color: #a0a0a0;*/
}
.message-main .message-info {
}
.conversation-message-avatar {
  width: 65px;
}
</style>


<script>
import _ from 'lodash'
import moment from 'moment'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import { v4 as uuidv4 } from 'uuid'

import Avatar from '@/components/avatar.vue'

import { AIMessageContentTypes, AIMessageRoleTypes, SSEChatEventTypes } from '@/store/types'

export default {
  name: 'AIChatArea',
  components: {
    Avatar
  },
  props: {
    agent: Object,
    conversationId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      chatMessageList: [],
      currentMessageData: '',
      currentMessageFormatted: '',
      thinkingMessage: '',
      chatMessage: '',
      isStreaming: false,
      displayScrollToBottomButton: false,

      conversation: null,

      markdownInstance: null
    }
  },
  computed: {
    hasConversation() {
      return !!this.conversationId
    },
    isChatDisabled() {
      return this.isStreaming
    },
  },
  watch: {
    conversationId: function() {
      this.requestData()
    }
  },
  mounted() {
    this.markdownInstance = new MarkdownIt({
      html: true,
      links: true,
      breaks: true
    })

    this.requestData()
  },
  methods: {
    requestData() {
      if(!this.conversationId)
        return

      this.chatMessageList = [];

      this.$apiService.aiService.getConversation(this.conversationId)
      .then(res => {
        this.conversation = res.data

        // Fill in the messages
        this.conversation.aiMessages.forEach(message => {
          // Ignore non-text messages
          if(message.contentType !== AIMessageContentTypes.text)
            return
          // Ignore system / report messages
          if(message.role === AIMessageRoleTypes.system || message.role === AIMessageRoleTypes.report)
            return

          this.addChatMessage(message)          
        })

        this.scrollChatToBottom()

        // If pending a report, start streaming the report
        // Done when first opening a conversation after starting a report
        if(this.conversation.isPendingReport) {
          this.submitMessage(true)
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to load conversation')
      })
      .finally(() => {
        // Any finalization if needed
      })
    },
    addChatMessage(message) {
      let messageInfo = {
        id: message.id,
        role: message.role,
        userTitle: '',
        userProfilePicURL: '',
        isAgent: message.role == AIMessageRoleTypes.assistant,
        renderedHtml: message.contentHTML || DOMPurify.sanitize(this.markdownInstance.render(message.content || '')),
        streaming: false,
        hasData: true,
        createdAt: message.createdAt
      }

      if(message.role == AIMessageRoleTypes.assistant) {
        messageInfo.userTitle = this.conversation.aiAgent.name

        messageInfo.userProfilePicURL = `/ai/images/${this.conversation.aiAgent.avatarType}.png`
      }
      else {
        let userAvatarURL = '/glass/img/user-overview-male.png'

        messageInfo.userTitle = message.user ? message.user.name : 'System'
        messageInfo.userProfilePicURL = userAvatarURL
      }

      this.chatMessageList.push(messageInfo)
    },
    async submitMessage(startReport = false) {
      if(!this.chatMessage && !startReport)
        return

      // Use the reporting API endpoint for chat
      // So the tools & reporting functions are on the reporting cluster & not the main API cluster
      // Note: This may introduce some replication delay before the message appears in the conversation, but allows for better scaling of streaming responses without impacting the main API cluster
      let finishStreamingDelay = 1000 // 1 second
      var chatURL = `${process.env.VUE_APP_REPORTING_API_URL}/ai/chat/stream`

      try {
        // Post to server
        var authToken = this.$authService.getAuthToken()

        var chatBody = {
          conversationId: this.conversation.id,
          message: this.chatMessage || '',
          startReport: startReport
        }

        this.isStreaming = true

        // We'll handle streaming directly via Fetch
        const controller = new AbortController();
        const response = await fetch(chatURL, {
          method: "POST",
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(chatBody),
          signal: controller.signal,
        });
        if(!response.ok) {
          const errorBody = await response.text();
          let errorMessage = `Unknown error during chat`;
          try {
            const errorData = JSON.parse(errorBody);
            if(errorData && errorData.error && errorData.error.message)
              errorMessage = errorData.error.message || errorMessage;
          } catch (err) {
          }
          
          this.isStreaming = false
          this.errorMessage('Chat error', errorMessage)
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        this.currentMessageData = '';
        this.currentMessageFormatted = '';
        this.thinkingMessage = '';

        // Clear message input
        this.chatMessage = '';

        let buffer = "";

        var processingMessage = null

        while (true) {
          const { value, done } = await reader.read();
          if(done)
            break;

          buffer += decoder.decode(value, { stream: true });

          const rawEvents = buffer.split("\n\n");
          buffer = rawEvents.pop() || "";

          for (const rawEvent of rawEvents) {
            const event = this.parseSseEvent(rawEvent);
            if (!event)
              continue;

            if (event.event === SSEChatEventTypes.started) {
              processingMessage = this.createStreamingMessage();
            }

            if (event.event === SSEChatEventTypes.userMessage) {
              this.addChatMessage(event.data);
              this.scrollChatToBottom()
            }

            if (event.event === SSEChatEventTypes.delta) {
              this.appendAssistantDelta(processingMessage, event.data.delta);
            }

            if (event.event === SSEChatEventTypes.completed) {
              this.finishAssistantMessage(processingMessage);
            }

            if(event.event == SSEChatEventTypes.toolStarted) {
              this.finishAssistantMessage(processingMessage);
              this.thinkingMessage = event.data.description
            }
            if(event.event == SSEChatEventTypes.toolCompleted) {
              this.thinkingMessage = ''

              processingMessage = this.createStreamingMessage();
              this.scrollChatToBottom()
            }

            if (event.event === SSEChatEventTypes.error) {
              console.error(event.data.message);
            }
          }
        }

        // Finish up
        // Introduce a slight delay before allowing new messages, to ensure the final message is rendered and any state is updated before allowing new input
        // And allow the messages to be replicated to the replica DB
        setTimeout(() => {
          this.isStreaming = false
          this.scrollChatToBottom()
        }, finishStreamingDelay)

      } catch(err) {
        this.isStreaming = false

        this.displayNetworkError(err, 'Error occurred during conversation chat')
      }
    },

    parseSseEvent(rawEvent) {
      const lines = rawEvent.split("\n");

      let event = "message";
      let data = "";

      for (const line of lines) {
        if (line.startsWith("event:")) {
          event = line.slice("event:".length).trim();
        }

        if (line.startsWith("data:")) {
          data += line.slice("data:".length).trim();
        }
      }

      if (!data)
        return null;

      return {
        event,
        data: JSON.parse(data),
      };
    },

    createStreamingMessage() {
      const message = {
        id: uuidv4(),
        rawContent: '',
        renderedHtml: '',
        streaming: true,
        hasData: false,
        role: AIMessageRoleTypes.assistant,
        userTitle: this.agent.name,
        createdAt: moment(),
        
        pendingText: '',
        renderQueued: false
      };

      this.chatMessageList.push(message);

      return message;
    },
    appendAssistantDelta(message, delta) {
      message.pendingText += delta;
      message.hasData = true;

      if(message.renderQueued)
        return

      message.renderQueued = true
      requestAnimationFrame(() => {
        message.renderQueued = false

        message.rawContent += message.pendingText
        message.pendingText = '';

        message.renderedHtml = DOMPurify.sanitize(this.markdownInstance.render(message.rawContent || ''));

        const ref = this.$refs["streamingMessage_" + message.id];
        const el = Array.isArray(ref) ? ref[0] : ref;

        if (el) {
          el.innerHTML = message.renderedHtml;
        }
      })
      this.scrollChatToBottom()
    },
    finishAssistantMessage(message) {
      message.streaming = false;
      message.hasData = true;

      if(message.pendingText) {
        message.rawContent += message.pendingText
        message.pendingText = ''

        message.renderedHtml = DOMPurify.sanitize(this.markdownInstance.render(message.rawContent || ''))
      }

      this.scrollChatToBottom()
    },
    isMessageFromAssistant(message) {
      return message.role == AIMessageRoleTypes.assistant
    },
    isMessageFromUser(message) {
      return message.role == AIMessageRoleTypes.user
    },
    displayMessageTimestamp(timestamp) {
      let messageTime = moment(new Date(timestamp)).local()

      // If not today, display date + time, otherwise just time
      if(!messageTime.isSame(moment().local(), 'day'))
        return messageTime.format('MMM D, YYYY h:mm A')

      return messageTime.format('h:mm A')
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if(this.$refs.chatArea)
            this.$refs.chatArea.scrollTop = this.$refs.chatArea.scrollHeight
        })
      })
    },
    onChatScroll() {
      // If user scrolls up, stop auto-scrolling to bottom until they scroll back down
      if(!this.$refs.chatArea)
        return

      const threshold = 100; // px from bottom to consider "at bottom"
      const position = this.$refs.chatArea.scrollTop + this.$refs.chatArea.clientHeight;
      const height = this.$refs.chatArea.scrollHeight;

      this.displayScrollToBottomButton = position + threshold < height;
    }
  }  
}
</script>